/** Shared enums and seed data for Organization Management (UI demo layer). */

/** Legacy / display only — Tenant is not assignable in organization master UI. */
export const ORG_TYPES = [
  { value: "Company", label: "Company" },
  { value: "Division", label: "Division" },
  { value: "Department", label: "Department" },
  { value: "Business Unit", label: "Business Unit" },
];

/**
 * Organization master create/edit dropdown (spec: Company, Division, Branch, Department, Section).
 * Tenant is excluded — it is a system boundary, not a user-selectable org type in master.
 */
export const ORG_TYPES_MASTER = [
  { value: "Company", label: "Company" },
  { value: "Division", label: "Division" },
  { value: "Branch", label: "Branch" },
  { value: "Department", label: "Department" },
  { value: "Section", label: "Section" },
  { value: "Business Unit", label: "Business Unit" },
];

export const ORG_STATUS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Blocked", label: "Blocked" },
  { value: "Archived", label: "Archived" },
];

export const CUSTOMER_TYPES = [
  { value: "Retailer", label: "Retailer" },
  { value: "Distributor", label: "Distributor" },
  { value: "Wholesaler", label: "Wholesaler" },
  { value: "Marketplace", label: "Marketplace" },
  { value: "Internal", label: "Internal" },
  { value: "Other", label: "Other" },
];

export const CUSTOMER_STATUS = [
  { value: "Draft", label: "Draft" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Blocked", label: "Blocked" },
];

/** Business segmentation (commercial / reporting). */
export const CUSTOMER_SEGMENTS = [
  { value: "", label: "— Select —" },
  { value: "Enterprise", label: "Enterprise" },
  { value: "SMB", label: "SMB" },
  { value: "Key account", label: "Key account" },
  { value: "Government", label: "Government" },
  { value: "Other", label: "Other" },
];

export const SALES_CHANNELS = [
  { value: "", label: "— Select —" },
  { value: "General Trade", label: "General Trade" },
  { value: "Modern Trade", label: "Modern Trade" },
  { value: "Ecommerce", label: "Ecommerce" },
  { value: "Export", label: "Export" },
  { value: "Other", label: "Other" },
];

/** Portal profile for customer users (UI placeholder until role API). */
export const PORTAL_USER_ROLES = [
  { value: "", label: "— Select —" },
  { value: "Buyer", label: "Buyer" },
  { value: "Admin", label: "Admin" },
  { value: "Viewer", label: "Viewer" },
];

export const PRICING_BASIS = [
  { value: "Customer", label: "Supplier specific" },
  { value: "Group", label: "Supplier group" },
  { value: "Contract", label: "Contract based" },
  { value: "Hybrid", label: "Hybrid" },
];

export const COUNTRIES = [
  { code: "IN", label: "India", flag: "🇮🇳" },
  { code: "US", label: "United States", flag: "🇺🇸" },
  { code: "GB", label: "United Kingdom", flag: "🇬🇧" },
  { code: "DE", label: "Germany", flag: "🇩🇪" },
  { code: "SG", label: "Singapore", flag: "🇸🇬" },
  { code: "AE", label: "United Arab Emirates", flag: "🇦🇪" },
];

export const TIME_ZONES = [
  "Asia/Kolkata",
  "America/New_York",
  "Europe/London",
  "Asia/Singapore",
  "Asia/Dubai",
];

export const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];

export const TYPE_ICON = {
  Tenant: "🏢",
  Company: "🏬",
  Division: "🧩",
  Branch: "🌿",
  Department: "📂",
  Section: "📑",
  "Business Unit": "⚙️",
  /** Synthetic tree row: supplier (API/customer record) under an organization */
  Supplier: "🏪",
};

/** Optional role for additional customer contacts. */
export const CONTACT_ROLES = [
  { value: "", label: "— Role —" },
  { value: "Billing", label: "Billing" },
  { value: "Operations", label: "Operations" },
  { value: "Buyer", label: "Buyer" },
  { value: "Other", label: "Other" },
];

/** Default maximum depth (config-driven in real apps). */
export const DEFAULT_MAX_ORG_LEVEL = 6;

/**
 * Seed flat organizations — replace with API integration.
 * @type {import('./orgTreeUtils').OrgFlatNode[]}
 */
export const INITIAL_ORG_FLAT = [
  {
    id: "org-root",
    code: "TNT-ACME",
    name: "Acme Corporation (Tenant)",
    shortName: "Acme",
    type: "Tenant",
    parentId: null,
    status: "Active",
    effectiveFrom: "2024-01-01",
    effectiveTo: "",
    legalEntity: true,
    operationalEntity: true,
    costCenter: "",
    businessUnit: "",
    departmentType: "",
    gstin: "",
    pan: "",
    country: "IN",
    state: "Maharashtra",
    city: "Mumbai",
    address1: "Tower A, BKC",
    address2: "",
    postalCode: "400051",
    timeZone: "Asia/Kolkata",
    currency: "INR",
    primaryContact: "Priya Nair",
    primaryEmail: "priya.nair@acme.example",
    primaryMobile: "+91-90000-00001",
    mappedUserIds: [],
    mappedCustomerIds: [],
  },
  {
    id: "org-comp",
    code: "CMP-RETAIL",
    name: "Acme Retail India",
    shortName: "Retail IN",
    type: "Company",
    parentId: "org-root",
    status: "Active",
    effectiveFrom: "2024-01-01",
    effectiveTo: "",
    legalEntity: true,
    operationalEntity: true,
    costCenter: "CC-RET-01",
    businessUnit: "Retail",
    departmentType: "",
    gstin: "27AAAAA0000A1Z5",
    pan: "AAAAA0000A",
    country: "IN",
    state: "Maharashtra",
    city: "Mumbai",
    address1: "Floor 12",
    address2: "BKC Complex",
    postalCode: "400051",
    timeZone: "Asia/Kolkata",
    currency: "INR",
    primaryContact: "Rahul Verma",
    primaryEmail: "rahul.verma@acme.example",
    primaryMobile: "+91-90000-00002",
    mappedUserIds: ["u1"],
    mappedCustomerIds: ["c1"],
    customers: [
      {
        customerId: "CUST-SEED-1",
        customerCode: "CUST-1001",
        customerName: "Metro Retail Pvt Ltd",
        customerShortName: "Metro",
        customerType: "Retailer",
        customerGroup: "GT-Premium",
        customerSegment: "Enterprise",
        salesChannel: "Modern Trade",
        marketRegion: "West",
        orgMappingId: "org-comp",
        parentCustomerId: "",
        billingName: "Metro Retail Pvt Ltd",
        billingAddressText: "Tower A, BKC\nMumbai, Maharashtra 400051",
        shippingAddressText: "Warehouse 12\nMumbai, Maharashtra 400051",
        orderCurrency: "INR",
        paymentTerms: "Net 30",
        creditLimit: "5000000",
        priceListId: "PL-RET-01",
        pricingBasis: "Group",
        portalLogin: true,
        portalUsernameEmail: "buyer@metro.example",
        portalUserRole: "Buyer",
        salesOrderEligible: true,
        customerStatus: "Active",
        taxRegistrationNumber: "27AAAAA0000A1Z5",
        panRegistrationRef: "AAAAA0000A",
        primaryContactName: "R. Mehta",
        primaryContactEmail: "r.mehta@metro.example",
        primaryContactPhone: "+91-90000-00101",
        customerContacts: [{ id: "ct-1", name: "Support", email: "support@metro.example", mobile: "+91-90000-00102", role: "Operations" }],
        customerEffectiveFrom: "2024-01-01",
        customerEffectiveTo: "",
        customerRemarks: "Key account",
        createdBy: "system",
        createdOn: "2024-01-01T00:00:00.000Z",
        updatedBy: "system",
        updatedOn: "2024-06-01T00:00:00.000Z",
        defaultWarehouse: "WH-MUM-01",
        salesOwner: "u2",
      },
    ],
  },
  {
    id: "org-div",
    code: "DIV-NORTH",
    name: "North Region",
    shortName: "North",
    type: "Division",
    parentId: "org-comp",
    status: "Active",
    effectiveFrom: "2024-04-01",
    effectiveTo: "",
    legalEntity: false,
    operationalEntity: true,
    costCenter: "CC-NR-01",
    businessUnit: "Retail",
    departmentType: "Region",
    gstin: "",
    pan: "",
    country: "IN",
    state: "Delhi",
    city: "New Delhi",
    address1: "Connaught Place",
    address2: "",
    postalCode: "110001",
    timeZone: "Asia/Kolkata",
    currency: "INR",
    primaryContact: "Neha Singh",
    primaryEmail: "neha.singh@acme.example",
    primaryMobile: "+91-90000-00003",
    mappedUserIds: [],
    mappedCustomerIds: [],
  },
  {
    id: "org-dept",
    code: "DEPT-SLS",
    name: "Sales Department",
    shortName: "Sales",
    type: "Department",
    parentId: "org-div",
    status: "Inactive",
    effectiveFrom: "2024-04-01",
    effectiveTo: "",
    legalEntity: false,
    operationalEntity: true,
    costCenter: "CC-SLS-01",
    businessUnit: "Retail",
    departmentType: "Sales",
    gstin: "",
    pan: "",
    country: "IN",
    state: "Delhi",
    city: "New Delhi",
    address1: "CP Annex",
    address2: "",
    postalCode: "110001",
    timeZone: "Asia/Kolkata",
    currency: "INR",
    primaryContact: "Amit Kumar",
    primaryEmail: "amit.kumar@acme.example",
    primaryMobile: "+91-90000-00004",
    mappedUserIds: ["u2"],
    mappedCustomerIds: ["c2"],
  },
  {
    id: "org-bu",
    code: "BU-ECOM",
    name: "E-Commerce BU",
    shortName: "ECOM",
    type: "Business Unit",
    parentId: "org-div",
    status: "Active",
    effectiveFrom: "2024-06-01",
    effectiveTo: "",
    legalEntity: false,
    operationalEntity: true,
    costCenter: "CC-EC-01",
    businessUnit: "Digital",
    departmentType: "Channel",
    gstin: "",
    pan: "",
    country: "IN",
    state: "Karnataka",
    city: "Bengaluru",
    address1: "Manyata Tech Park",
    address2: "",
    postalCode: "560045",
    timeZone: "Asia/Kolkata",
    currency: "INR",
    primaryContact: "Sana Khan",
    primaryEmail: "sana.khan@acme.example",
    primaryMobile: "+91-90000-00005",
    mappedUserIds: [],
    mappedCustomerIds: [],
  },
];

export const MOCK_USERS = [
  { value: "u1", label: "Priya Nair (Admin)" },
  { value: "u2", label: "Amit Kumar (Sales)" },
  { value: "u3", label: "Deepa Iyer (Finance)" },
  { value: "u4", label: "John Smith (Ops)" },
];

export const MOCK_CUSTOMERS = [
  { value: "c1", label: "CUST-1001 — Metro Retail Pvt Ltd" },
  { value: "c2", label: "CUST-1002 — FreshMart Distributors" },
  { value: "c3", label: "CUST-1003 — Urban Lifestyle Stores" },
];
